<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { PrimaryButton, QuestionPhone, StarsRate, useTranslation } from '@hfc/ui-core';
import { useHandleApiError } from '@/shared/infrastructure/composition/useHandleApiError';
import { UUID } from '@hfc/common/src/types/UUID';
import { useForm, Field } from 'vee-validate';
import { useAppStore } from '@/shared/infrastructure/composition/useAppStore';
import { APPLICATION_TYPES } from '@hfc/common/src/types/ApplicationTypes';
import { FeedbackInfo } from '@/application/shared/domain/FeedbackInfo';
import GoogleRecaptcha from '@/shared/infrastructure/components/GoogleRecaptcha.vue';

const { appStore, appState } = useAppStore();

const { handleSubmit } = useForm();
const { handleSfApiError } = useHandleApiError();
const { t } = useTranslation();

const props = withDefaults(defineProps<{
  applicationType?: APPLICATION_TYPES;
  uuid?: UUID;
  showTitle?: boolean;
  showShadow?: boolean;
  extraParams?: unknown;
  token?: string;
}>(), {
  showTitle: true,
  showShadow: true,
});

interface Emits {
  (event: 'feedback-success'): void
}
const emits = defineEmits<Emits>();

const localContent = ref();
const localPhone = ref();
const localRating = ref();

const firstName = appState.value.feedbackUserData?.first_name;
const token = appState.value.feedbackUserData?.token || props.token;

const recaptchaRef = ref<InstanceType<typeof GoogleRecaptcha> | null>(null);
const recaptchaToken = ref('');

const contentId = computed(() => `feedback-text-${Date.now()}-${Math.random().toString(36)}`);

const onSubmit = async (): Promise<void> => {
  if (!recaptchaToken.value) {
    return;
  }

  const params: FeedbackInfo = {
    content: localContent.value,
    phone: localPhone.value?.number.toString(),
    rating: localRating.value,
    captcha_token: recaptchaToken.value
  };

  if (props.extraParams) {
    params.extra_params = props.extraParams;
  }

  let success, error;
  if (props.applicationType && props.uuid) {
    ({ success, error } = await appStore.sendApplicationFeedback(params, props.applicationType, props.uuid, token));
  } else {
    ({ success, error } = await appStore.sendAnonymousFeedback(params));
  }

  if (error) {
    return handleSfApiError(error, true);
  }

  if (success) {
    emits('feedback-success');
  }
};

const onCaptchaSuccess = (token: string | null): void => {
  if (token) {
    recaptchaToken.value = token;
    onSubmit();
  }
};

const beforeSubmit = handleSubmit(async () => {
  recaptchaRef.value?.executeRecaptcha();
});

onMounted(() => {
  recaptchaRef.value?.renderRecaptcha();
});

</script>
<template>
  <div>
    <textarea id="feedback-content" name="feedback-content" class="custom-hidden"></textarea>
    <div v-if="showTitle" class="py-10">
      <h1 class="text-center">{{ t('feedback.title') }}</h1>
    </div>
    <form @submit.prevent="beforeSubmit" @keydown.enter.prevent="beforeSubmit" class="flex flex-col items-center">
      <div class="flex flex-col w-full bg-white rounded-lg p-4 sm:p-6 lg:w-[500px]" :class="{ 'shadow-lg': showShadow }">
        <div class="flex flex-col space-y-8">
          <div>
            <div>
              <label class="font-medium mb-1 text-sky-700 block" v-if="firstName">
                {{ t('common.greeting', { name: firstName }) }}
              </label>
              <Field v-model="localContent" v-slot="{ field, errorMessage, meta }" :name="contentId"
                :rules="{ required: true }">
                <textarea :id="contentId" class="h-full mt-4" :class="[meta]" v-bind="field"
                  :placeholder="t('feedback.contentPlaceholder')" type="textarea" rows="3" />
                <span v-if="errorMessage" class="error">{{ errorMessage }}</span>
              </Field>
            </div>
          </div>
          <div v-if="!token">
            <QuestionPhone v-model="localPhone" :hide-ext="true" :hide-type="true" @update:value="localPhone = $event" :required-phone="false" :phone-input-placeholder="t('feedback.phonePlaceholder')" />
          </div>
          <div v-if="localContent">
            <div>
              <label class="font-medium mb-4 text-sky-700 block">
                {{ t('feedback.ratingPrompt') }}
              </label>
              <Field v-model="localRating" v-slot="{ errorMessage }" name="feedback-star-rating" :rules="{ required: true }">
                <StarsRate :editable="true" :show-text="true" :icon-size="'text-2xl'" @popularity-rate-change="(val: number) => localRating = val" />
                <span v-if="errorMessage" class="error">{{ errorMessage }}</span>
              </Field>
              <div id="starsRate"></div>
            </div>
          </div>
          <GoogleRecaptcha container-id="feedback-modal-recaptcha-container" @captcha-success="onCaptchaSuccess" ref="recaptchaRef" />
        </div>
      </div>
      <div class="flex justify-center mt-10 mb-10">
        <PrimaryButton @click="beforeSubmit" size="xl" :is-loading="appState.loading"
          :disabled="appState.loading">
          <span>{{ t('common.send') }}</span>
        </PrimaryButton>
      </div>
    </form>
  </div>
</template>
